/*
 * Companies (HQ, brands, locations, franchises) and their settings.
 */
import { z } from "zod";
import { stewardMark } from "./client-look.js";
import { TRADES } from "./core.js";
import { companyLevelSchema } from "./levels.js";
import { settingsPatchSchema } from "./settings.js";

const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a color like #1F6F5C.");

const companyFields = {
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().max(20).nullable(),
  email: z.email().nullable(),
  /** The address in parts (address.ts): address is Street 1. */
  address: z.string().trim().max(200).nullable(),
  address2: z.string().trim().max(100).nullable(),
  city: z.string().trim().max(100).nullable(),
  state: z
    .string()
    .trim()
    .regex(/^[A-Za-z]{2}$/, "Use the 2 letter state, like LA.")
    .transform((s) => s.toUpperCase())
    .nullable(),
  zip: z
    .string()
    .trim()
    .regex(/^\d{5}$/, "Use the 5 digit ZIP.")
    .nullable(),
  website: z.url().nullable(),
  brandColor: hexColor.nullable(),
  /** The brand kit: Pay and Approve buttons, links and second buttons, small marks. Null uses the brand color. */
  ctaColor: hexColor.nullable(),
  secondaryColor: hexColor.nullable(),
  accentColor: hexColor.nullable(),
  businessType: z.enum(["home", "business"]),
  timezone: z.string().refine((tz) => {
    try {
      new Intl.DateTimeFormat("en-US", { timeZone: tz });
      return true;
    } catch {
      return false;
    }
  }, "That is not a time zone."),
};

/** Adding a company. Its short name (for web addresses) is made by the server: brand-name, or brand-name-2 for a location. */
export const companyCreateBody = z.object({
  ...companyFields,
  trade: z.enum(TRADES),
  phone: companyFields.phone.default(null),
  email: companyFields.email.default(null),
  address: companyFields.address.default(null),
  address2: companyFields.address2.default(null),
  city: companyFields.city.default(null),
  state: companyFields.state.default(null),
  zip: companyFields.zip.default(null),
  website: companyFields.website.default(null),
  brandColor: companyFields.brandColor.default(null),
  ctaColor: companyFields.ctaColor.default(null),
  secondaryColor: companyFields.secondaryColor.default(null),
  accentColor: companyFields.accentColor.default(null),
  businessType: companyFields.businessType.default("home"),
  timezone: companyFields.timezone.default("America/Chicago"),
});

/** Changing a company. Nothing is ever deleted; status paused stops it instead. */
export const companyUpdateBody = z
  .object({
    ...companyFields,
    status: z.enum(["active", "paused"]),
    /** The company's own web address (https://app.thecleanersusa.com). Head office sets it; null uses the one above. */
    appUrl: z.url().nullable(),
    /** Where client links live (https://my.thegroundsmen.com), when not the web address above. Null uses that one. */
    clientUrl: z.url().nullable(),
    /** A picture of the logo for the sign-in page, as an https address. */
    logoUrl: z.url({ protocol: /^https$/ }).nullable(),
    /** The all-white logo for the brand color band. Only cleared here; uploading sets it. */
    logoWhiteUrl: z.url({ protocol: /^https$/ }).nullable(),
  })
  .partial()
  .strict();

/** Uploading a brand's logo. A picture only (no SVG, which can carry script); the server keeps it and links to it. */
export const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export const logoUpload = z.object({
  name: z.string().trim().min(1).max(200),
  mime: z.enum(LOGO_TYPES, "Use a PNG, JPG or WebP picture."),
  dataBase64: z.string().max(2_800_000, "That picture is too big. The most is 2 MB."),
  /** color for white backgrounds, white for the brand color band. */
  variant: z.enum(["color", "white"]).default("color"),
});

export const companyAnswer = z.object({
  id: z.uuid(),
  parentId: z.uuid().nullable(),
  name: z.string(),
  slug: z.string(),
  trade: z.enum(TRADES),
  status: z.enum(["active", "paused"]),
  timezone: z.string(),
  brandColor: z.string().nullable(),
  ctaColor: z.string().nullable(),
  secondaryColor: z.string().nullable(),
  accentColor: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  website: z.string().nullable(),
  businessType: z.enum(["home", "business"]),
  role: z.string().nullable(),
  /** hq, brand, or location (a location or franchise). Decides which business info and keys the company sets itself. */
  level: companyLevelSchema,
  /** An owner from above working in a franchise that keeps its customer names from head office (draft 11). */
  namesPrivate: z.boolean(),
  /** The company's own web address, if head office set one. */
  appUrl: z.string().nullable(),
  /** Where its app lives: its own address, else its brand's, else head office's. */
  linkUrl: z.string(),
  /** Its own client address, if head office set one. */
  clientUrl: z.string().nullable(),
  /** Where its client links live: the nearest client address up the tree, else linkUrl. */
  clientLinkUrl: z.string(),
  logoUrl: z.string().nullable(),
  logoWhiteUrl: z.string().nullable(),
});

/** The sign-in page's look at a web address: the brand there, or null for the plain Steward page. */
export const signInLookAnswer = z.object({
  brand: z
    .object({
      name: z.string(),
      color: z.string().nullable(),
      logoUrl: z.string().nullable(),
      /** Shown on the color band before the color logo. */
      logoWhiteUrl: z.string().nullable(),
      /** The tab icon at this address: the Steward mark in the brand's colors. */
      mark: stewardMark,
    })
    .nullable(),
  /** Head office's address, which holds the shared sign in for every brand's address. Missing at head office itself. */
  sso: z.string().nullable().optional(),
});

/** Where a signed-in login starts at the address they opened (companies/brand-address.ts on the server). */
export const landingAnswer = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("open"), companyId: z.uuid().nullable() }),
  z.object({ kind: z.literal("brand"), companyId: z.uuid(), brand: z.string() }),
  z.object({
    kind: z.literal("no_access"),
    brand: z.string(),
    elsewhere: z.array(z.object({ name: z.string(), url: z.string() })),
  }),
]);

export const originQuery = z.object({ origin: z.string().trim().min(1).max(200) });

export const settingsUpdateBody = settingsPatchSchema;

/** One company in the report across child companies. */
export const companySummaryRow = z.object({
  companyId: z.uuid(),
  name: z.string(),
  parentId: z.uuid().nullable(),
  clients: z.number(),
  activeJobs: z.number(),
});
